import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Create new tenancy after advance payment verified
export const CreateTenancy = createAsyncThunk(
  "tenancy/create",
  async ({ tenancyData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/tenancy/create`, tenancyData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to create tenancy" });
    }
  }
);

// Get all tenancies for a tenant
export const GetTenantTenancies = createAsyncThunk(
  "tenancy/getTenantTenancies",
  async ({ tenantId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/tenancy/tenant/${tenantId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch tenancies" });
    }
  }
);

// Get all tenancies for a landlord
export const GetLandlordTenancies = createAsyncThunk(
  "tenancy/getLandlordTenancies",
  async ({ landlordId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/tenancy/landlord/${landlordId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch tenancies" });
    }
  }
);

// Get single tenancy details
export const GetTenancyDetails = createAsyncThunk(
  "tenancy/getDetails",
  async ({ tenancyId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/tenancy/${tenancyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch tenancy details" });
    }
  }
);

// End tenancy
export const EndTenancy = createAsyncThunk(
  "tenancy/end",
  async ({ tenancyId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/api/tenancy/${tenancyId}/end`, { reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to end tenancy" });
    }
  }
);

const TenancySlice = createSlice({
  name: "tenancy",
  initialState: {
    loading: false,
    tenancies: [],
    activeTenancy: null,
    currentTenancy: null,
    error: "",
    success: false,
  },
  reducers: {
    resetTenancyState: (state) => {
      state.loading = false;
      state.error = "";
      state.success = false;
      state.currentTenancy = null;
    },
    setActiveTenancy: (state, action) => {
      state.activeTenancy = action.payload;
    },
    clearTenancyError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    // Create Tenancy
    builder.addCase(CreateTenancy.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.success = false;
    });
    builder.addCase(CreateTenancy.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.currentTenancy = action.payload?.data;
      if (action.payload?.data) {
        state.tenancies.push(action.payload.data);
      }
    });
    builder.addCase(CreateTenancy.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to create tenancy";
    });

    // Get Tenant Tenancies
    builder.addCase(GetTenantTenancies.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetTenantTenancies.fulfilled, (state, action) => {
      state.loading = false;
      state.tenancies = action.payload?.data || [];
      // Set active tenancy if exists
      const active = state.tenancies.find(t => t.status === 'active');
      if (active) {
        state.activeTenancy = active;
      }
    });
    builder.addCase(GetTenantTenancies.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch tenancies";
    });

    // Get Landlord Tenancies
    builder.addCase(GetLandlordTenancies.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetLandlordTenancies.fulfilled, (state, action) => {
      state.loading = false;
      state.tenancies = action.payload?.data || [];
    });
    builder.addCase(GetLandlordTenancies.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch tenancies";
    });

    // Get Tenancy Details
    builder.addCase(GetTenancyDetails.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetTenancyDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.currentTenancy = action.payload?.data;
    });
    builder.addCase(GetTenancyDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch tenancy details";
    });

    // End Tenancy
    builder.addCase(EndTenancy.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(EndTenancy.fulfilled, (state, action) => {
      state.loading = false;
      const endedId = action.payload?.data?._id;
      state.tenancies = state.tenancies.map(t =>
        t._id === endedId ? { ...t, status: 'ended' } : t
      );
      if (state.activeTenancy?._id === endedId) {
        state.activeTenancy = null;
      }
    });
    builder.addCase(EndTenancy.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to end tenancy";
    });
  },
});

export const { resetTenancyState, setActiveTenancy, clearTenancyError } = TenancySlice.actions;
export default TenancySlice.reducer;
